import { useState } from 'react';

import { useTranslation } from 'react-i18next';

import { useMediaQuery } from '@/hooks';
import ChevronDown from '@/icons/chevron-down.svg';
import ChevronUp from '@/icons/chevron-up.svg';
import EditPDFIcon from '@/icons/edit-pdf.svg';
import SupportIcon from '@/icons/support.svg';
import { cn } from '@/utils';

import Button from '../Button';
import { Collapsible, CollapsibleContent } from '../Collapsible';
import DetailItem from './DetailItem';

const iconsMapping = {
	trial: EditPDFIcon,
	service: SupportIcon
};

function DetailCard({
	title,
	description,
	priceText,
	features = [],
	planKey,
	footerComponent = null
}) {
	const { t } = useTranslation();
	const [open, setOpen] = useState(false);

	const mdDown = useMediaQuery('mdDown');

	const toggleOpen = () => setOpen(prevState => !prevState);

	const dropdownIcon = open ? <ChevronUp /> : <ChevronDown />;

	const IconComponent = planKey && iconsMapping[planKey];

	return (
		<div className="flex flex-col rounded-md border border-grey-200 pt-3 md:pt-4">
			<div className="flex items-start justify-between gap-2 px-3 md:gap-4 md:px-4">
				<div className="flex max-w-[300px] flex-col gap-1 md:max-w-[initial]">
					{title && (
						<p className="block text-sm font-semibold leading-[28px] text-black">
							{title}
						</p>
					)}

					{description && (
						<p className="block text-xs leading-[20px] text-grey-500">
							{description}
						</p>
					)}
				</div>

				{IconComponent && (
					<div className="flex h-[40px] w-[40px] shrink-0 items-center justify-center rounded-sm bg-red-background p-2 text-red md:h-[48px] md:w-[48px]">
						<IconComponent />
					</div>
				)}
			</div>

			<Collapsible open={open} onOpenChange={setOpen}>
				<CollapsibleContent className="collapsibleContent mt-3 w-full px-3 md:mt-4 md:px-4">
					<div className="flex flex-col gap-3 md:gap-4">
						{features?.length &&
							features.map((feature, index) => (
								<DetailItem key={`DetailItem--${index}`} {...feature} />
							))}
					</div>
				</CollapsibleContent>
			</Collapsible>

			<div
				className={cn(
					'align-items flex w-full items-center justify-between border-t border-grey-200 px-3 py-3 md:px-4',
					open && !mdDown ? 'mt-4' : 'mt-3'
				)}
			>
				<div className="flex items-center gap-6">
					{priceText && (
						<span className="text-sm font-semibold">{priceText}</span>
					)}

					{footerComponent}
				</div>

				{mdDown ? (
					<Button variant="outline" size="iconSm" onClick={toggleOpen}>
						{dropdownIcon}
					</Button>
				) : (
					<Button
						variant="ghost"
						size="xs"
						onClick={toggleOpen}
						endIcon={dropdownIcon}
						className={cn('[&_svg]:size-2', open ? '[&_svg]:size-4' : '')}
					>
						{t('paymentBreakdown.details')}
					</Button>
				)}
			</div>
		</div>
	);
}

export default DetailCard;
